import React, { useCallback, useEffect, useState } from 'react';

import AdminDataTable from '../../components/admin/AdminDataTable';
import { formatAdminDate, formatAdminDateTime } from '../../components/admin/adminFormatting';
import AdminPagination from '../../components/admin/AdminPagination';
import Badge from '../../components/common/Badge';
import { Button, PageHeader } from '@copilot/ui';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import * as adminApi from '../../services/adminApi';

const PAGE_SIZE = 20;

// models/release_announcement.py's ANNOUNCEMENT_* statuses. A send that
// finished with failures is called out so it is not shown as a clean one.
function statusOf(item) {
  if (item.status === 'notes_error') return { label: 'Not sent', variant: 'disconnected' };
  if (item.status === 'sending') return { label: 'Sending', variant: 'reconnecting' };
  if (item.failed_count > 0) return { label: 'Completed with failures', variant: 'paused' };
  return { label: 'Completed', variant: 'listening' };
}

const COLUMNS = [
  {
    key: 'version',
    label: 'Release Version',
    render: (item) => (
      <>
        <div>{item.version}</div>
        {item.title && <div className="admin-table-subtext">{item.title}</div>}
      </>
    ),
  },
  { key: 'released_at', label: 'Release Date', render: (item) => formatAdminDate(item.released_at) },
  {
    key: 'status',
    label: 'Email Status',
    render: (item) => {
      const { label, variant } = statusOf(item);
      return (
        <>
          <Badge variant={variant}>{label}</Badge>
          {item.error && <div className="admin-table-subtext">{item.error}</div>}
        </>
      );
    },
  },
  { key: 'eligible_count', label: 'Eligible Users' },
  { key: 'sent_count', label: 'Sent' },
  { key: 'failed_count', label: 'Failed' },
  { key: 'skipped_count', label: 'Skipped' },
  {
    key: 'announced_at',
    label: 'Announced',
    render: (item) => formatAdminDateTime(item.announced_at),
  },
];

function AdminReleaseNotificationsPage() {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [testVersion, setTestVersion] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError('');

      try {
        const data = await adminApi.getReleaseNotifications({ page, page_size: PAGE_SIZE });
        if (!cancelled) {
          setItems(data.items);
          setTotal(data.total);
          setTotalPages(data.total_pages);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.status === 403
              ? 'You do not have permission to view release emails.'
              : err.message || 'Unable to load release emails.'
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [page]);

  // Sending happens in the background: keep the counts moving while the page is open.
  const refresh = useCallback(
    async ({ isStale }) => {
      const data = await adminApi.getReleaseNotifications({ page, page_size: PAGE_SIZE });
      if (!isStale()) {
        setItems(data.items);
        setTotal(data.total);
        setTotalPages(data.total_pages);
      }
    },
    [page]
  );

  useAutoRefresh(refresh);

  const sendTest = async (event) => {
    event.preventDefault();
    setIsSending(true);
    setTestResult(null);

    try {
      const result = await adminApi.sendReleaseTestEmail(testVersion.trim());
      setTestResult({ ok: true, message: `Test email sent to ${result.sent_to}.` });
    } catch (err) {
      setTestResult({ ok: false, message: err.message || 'The test email could not be sent.' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Release Emails"
        description="Announcements are sent automatically when a desktop release is published, from release-notes/v<version>.txt."
      />

      <form className="admin-toolbar" onSubmit={sendTest}>
        <div className="admin-toolbar-filter">
          <input
            className="input"
            placeholder="Version, e.g. 2.1.0"
            value={testVersion}
            onChange={(e) => setTestVersion(e.target.value)}
            aria-label="Release version for the test email"
          />
        </div>
        <Button type="submit" variant="secondary" disabled={isSending || !testVersion.trim()}>
          {isSending ? 'Sending...' : 'Send test email to me'}
        </Button>
      </form>
      {testResult && (
        <p className={`admin-action-status ${testResult.ok ? 'success' : 'error'}`} role="status">
          {testResult.message}
        </p>
      )}

      {error ? (
        <div className="form-alert">{error}</div>
      ) : (
        <>
          <AdminDataTable
            columns={COLUMNS}
            rows={items}
            getRowKey={(item) => item.id}
            isLoading={isLoading}
            loadingLabel="Loading release emails..."
            emptyTitle="No releases have been announced yet."
            emptyDescription="A row appears when a desktop release is published."
          />

          {!isLoading && (
            <AdminPagination
              page={page}
              totalPages={totalPages}
              total={total}
              itemLabel="releases"
              onPageChange={setPage}
              ariaLabel="Release email pages"
            />
          )}
        </>
      )}
    </div>
  );
}

export default AdminReleaseNotificationsPage;
